import * as React from "react";
import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";

interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string | number;
  label: string;
  icon?: LucideIcon;
  trend?: {
    value: number;
    positive: boolean;
  };
  size?: "sm" | "md" | "lg";
}

const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, value, label, icon: Icon, trend, size = "md", ...props }, ref) => {
    const sizeClasses = {
      sm: "p-4",
      md: "p-6",
      lg: "p-8",
    };

    const valueSizeClasses = {
      sm: "text-2xl",
      md: "text-4xl",
      lg: "text-5xl",
    };

    return (
      <div
        ref={ref}
        className={cn("stat-card", sizeClasses[size], className)}
        {...props}
      >
        {Icon && (
          <div className="mb-3 p-2 rounded-xl bg-[hsl(var(--color-4))]/10">
            <Icon className="h-5 w-5 text-[hsl(var(--color-4))]" />
          </div>
        )}
        <div className={cn("font-bold text-[hsl(var(--color-7))]", valueSizeClasses[size])}>
          {value}
        </div>
        <div className="text-sm text-[hsl(var(--color-8))] mt-1">{label}</div>
        {trend && (
          <div
            className={cn(
              "mt-2 text-xs font-medium flex items-center gap-1",
              trend.positive ? "text-emerald-600" : "text-red-600"
            )}
          >
            <span>{trend.positive ? "↑" : "↓"}</span>
            <span>{Math.abs(trend.value)}%</span>
          </div>
        )}
      </div>
    );
  }
);
StatCard.displayName = "StatCard";

export { StatCard };
export type { StatCardProps };
